import styled from "@emotion/styled";
import React from "react";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { doNothing } from "../../../utils/doNothing";
import { AppButton } from "../../Generic/AppButton";
import { AppCard, AppCardContent } from "../../Generic/AppCard";
import Flex from "../../Generic/Flex";
import NextButton from "../../NextButton/NextButton";
import { CircleShimmer, TextShimmer } from "../../Shimmer";
import { FilledCircledDot } from "./icons/FilledCircleDot";
import { FilledCircledMinus } from "./icons/FilledCircleMinus";
import { FilledCircledTick } from "./icons/FilledCircleTick";
import { FilledCircledX } from "./icons/FilledCircleX";

type Props =
  | {
      isLoading: true;
    }
  | {
      isLoading: false;
      title: string;
      subTitle: string;
      icon: "success" | "error" | "warning" | "info" | null;
      errorMessage: string | null;
      footer:
        | {
            type: "fixIssue";
            text: string;
          }
        | {
            type: "view";
          };
      onClick: () => void;
    };

function ComplianceItemCardUI(props: Props) {
  if (props.isLoading) {
    return (
      <ComplianceItemCardUI.Styled onClick={doNothing}>
        <AppCardContent>
          <ComplianceItemCardUI.ContentWrapper>
            <Flex xGap={8} alignItems="center">
              <ComplianceItemCardIcon {...props} />
              <ComplianceItemCardUI.Title>
                <TextShimmer width={30} height={14} />
              </ComplianceItemCardUI.Title>
            </Flex>
            <ComplianceItemCardUI.Subtitle>
              <TextShimmer width={60} height={14} />
            </ComplianceItemCardUI.Subtitle>
            <ComplianceItemCardFooter {...props} />
          </ComplianceItemCardUI.ContentWrapper>
        </AppCardContent>
      </ComplianceItemCardUI.Styled>
    );
  }

  return (
    <ComplianceItemCardUI.Styled onClick={props.onClick}>
      <AppCardContent>
        <ComplianceItemCardUI.ContentWrapper>
          <Flex xGap={8} alignItems="center">
            <ComplianceItemCardIcon {...props} />
            <ComplianceItemCardUI.Title>{props.title}</ComplianceItemCardUI.Title>
          </Flex>
          <ComplianceItemCardUI.Subtitle>{props.subTitle}</ComplianceItemCardUI.Subtitle>
          {props.errorMessage !== null && (
            <ErrorMessageBadgeWrapper>
              <ErrorMessageBadge>{props.errorMessage}</ErrorMessageBadge>
            </ErrorMessageBadgeWrapper>
          )}
          <ComplianceItemCardFooter {...props} />
        </ComplianceItemCardUI.ContentWrapper>
      </AppCardContent>
    </ComplianceItemCardUI.Styled>
  );
}

const ErrorMessageBadge = styled.div`
  border: 1px solid var(--tw-red-400);
  background-color: var(--tw-red-100);
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 14px;
`;

const ErrorMessageBadgeWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

function ComplianceItemCardIcon(props: Props) {
  if (props.isLoading) {
    return <CircleShimmer width={16} height={16} />;
  }

  switch (props.icon) {
    case null:
      return null;

    case "success":
      return <FilledCircledTick />;

    case "error":
      return <FilledCircledX />;

    case "warning":
      return <FilledCircledMinus />;

    case "info":
      return <FilledCircledDot />;
  }
}

function ComplianceItemCardFooter(props: Props) {
  if (props.isLoading) {
    return (
      <ComplianceItemCardFooterWrapper>
        <ComplianceItemCardFooterText></ComplianceItemCardFooterText>
        <TextShimmer width={80} height={32} />
      </ComplianceItemCardFooterWrapper>
    );
  }

  switch (props.footer.type) {
    case "fixIssue":
      return (
        <ComplianceItemCardFooterWrapper>
          <ComplianceItemCardFooterText>{props.footer.text}</ComplianceItemCardFooterText>
          <ComplianceItemCardButton size="extra-small">
            {translate(STRINGS.FIX_ISSUE)}
          </ComplianceItemCardButton>
        </ComplianceItemCardFooterWrapper>
      );

    case "view":
      return (
        <ComplianceItemCardFooterWrapper>
          <ComplianceItemCardFooterText></ComplianceItemCardFooterText>
          <ComplianceItemCardButton size="extra-small">View</ComplianceItemCardButton>
        </ComplianceItemCardFooterWrapper>
      );
  }
}

const ComplianceItemCardButton = styled(AppButton)`
  border-radius: 12px;
`;

const ComplianceItemCardFooterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  gap: 4px;
`;

const ComplianceItemCardFooterText = styled.span`
  font-size: 14px;
  font-weight: 300;
  flex: 1;
`;

ComplianceItemCardUI.ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

ComplianceItemCardUI.Title = styled.span`
  font-size: 16px;
  font-weight: 500;
`;

ComplianceItemCardUI.Subtitle = styled.span`
  font-weight: 300;
  font-size: 14px;
  margin-inline-start: 24px;
`;

ComplianceItemCardUI.TextMuted = styled.span`
  color: var(--tw-gray-700);
`;

ComplianceItemCardUI.Styled = styled(AppCard)`
  .AppCardContent {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  ${NextButton.Styled} {
    flex-shrink: 0;
  }

  ${ComplianceItemCardUI.ContentWrapper} {
    flex: 1;
  }
`;

export default ComplianceItemCardUI;
